import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Share2, CheckCircle, XCircle, ExternalLink } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { useMyWhatsAppInstance } from '@/hooks/useMyWhatsAppInstance';
import { useWhatsAppServers } from '@/hooks/useWhatsAppServers';
import { toast } from 'sonner';

/**
 * Component for non-admin users to activate the shared WhatsApp session.
 * Shown in WhatsApp Settings when user is not an admin.
 */
export const SettingsSharedConnection = () => {
  const { instance, loading, createOrConnect, disconnect, loadMyInstance } = useMyWhatsAppInstance();
  const { sharedServer, sharedServerLoading } = useWhatsAppServers();
  const [isActivating, setIsActivating] = useState(false);

  const isUsingShared = instance?.use_shared_session && instance?.connection_status === 'connected';
  const hasPersonalConnection = instance && !instance.use_shared_session && instance.connection_status === 'connected';
  const sharedAvailable = sharedServer && !sharedServerLoading;

  const handleActivateShared = async () => {
    if (!sharedServer) {
      toast.error('Número central não disponível');
      return;
    }

    setIsActivating(true);
    try {
      await createOrConnect(
        `shared_${Date.now()}`, // Instance name for shared (not really used)
        sharedServer.id,
        true // useSharedSession = true
      );
      toast.success('Número central ativado com sucesso!');
      await loadMyInstance();
    } catch (error) {
      console.error('Error activating shared session:', error);
      toast.error('Erro ao ativar número central');
    } finally {
      setIsActivating(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      await disconnect();
      toast.success('Número central desativado');
    } catch (error) {
      console.error('Error deactivating:', error);
    }
  };

  if (loading || sharedServerLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Share2 className="h-5 w-5" />
          Usar Número Central
        </CardTitle>
        <CardDescription>
          Envie mensagens através do número compartilhado da empresa
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status atual */}
        {isUsingShared ? (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800 flex items-center gap-2">
              Você está usando o 
              <Badge variant="secondary">
                <Share2 className="h-3 w-3 mr-1" />
                Número Central
              </Badge>
            </AlertDescription>
          </Alert>
        ) : hasPersonalConnection ? (
          <Alert className="bg-blue-50 border-blue-200">
            <CheckCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              Você está usando seu <strong>número pessoal</strong>. 
              Para mudar, desconecte primeiro no seu Perfil.
            </AlertDescription>
          </Alert>
        ) : !sharedAvailable ? (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>
              O número central da empresa ainda não foi configurado. 
              Entre em contato com o administrador.
            </AlertDescription>
          </Alert>
        ) : (
          <Alert>
            <Share2 className="h-4 w-4" />
            <AlertDescription>
              Ao ativar o número central, suas mensagens serão enviadas através 
              do WhatsApp da empresa.
            </AlertDescription>
          </Alert>
        )}

        {/* Ações */}
        <div className="space-y-3">
          {isUsingShared ? (
            <Button
              onClick={handleDeactivate}
              variant="destructive"
              className="w-full"
            >
              Desativar Número Central
            </Button>
          ) : (
            <Button
              onClick={handleActivateShared}
              disabled={!sharedAvailable || hasPersonalConnection || isActivating}
              size="lg"
              className="w-full"
            >
              {isActivating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Share2 className="mr-2 h-4 w-4" />
              )}
              Ativar Número Central
            </Button>
          )}
        </div>

        {/* Link para Perfil */}
        <div className="pt-4 border-t">
          <p className="text-sm text-muted-foreground mb-2">
            Para conectar seu próprio número WhatsApp:
          </p>
          <Link 
            to="/profile"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            <ExternalLink className="h-3 w-3" />
            Ir para Perfil → Integrações
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};
